import React, { useRef, useEffect, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";

if (Quill && typeof Quill.register === "function") {
  Quill.register("modules/imageResize", ImageResize);
}

const Delta = Quill.import("delta");

// -------------------------------
// Quill Modules & Formats
// -------------------------------

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link", "video"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "indent",
  "color",
  "background",
  "align",
  "link",
  "image",
  "video",
];

// -------------------------------
// Rich Text Editor Component
// -------------------------------

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Enter text...",
  readOnly = false,
  label = null,
  height = "250px",
  className = "",
  onImagePaste, // Custom callback for handling pasted images (optional)
  debugId = "RichTextEditor",
}) => {
  const quillRef = useRef(null);
  // Keep the latest onImagePaste to avoid stale closures when the prop changes
  const onImagePasteRef = useRef(onImagePaste);
  useEffect(() => {
    onImagePasteRef.current = onImagePaste;
  }, [onImagePaste]);

  console.info("[RichTextEditor] render", {
    debugId,
    label,
    hasOnImagePaste: !!onImagePaste,
  });

  const handlePaste = useCallback((e) => {
      console.info("[RichTextEditor] paste event received", { debugId });
      const items = e.clipboardData?.items;
      const filesFallback = e.clipboardData?.files;
      if (!items && !filesFallback) {
        console.info("[RichTextEditor] no clipboard items", { debugId });
        return;
      }

      let imageItem = null;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].type.indexOf('image') !== -1) {
            imageItem = items[i];
            break;
          }
        }
      }
      if (!imageItem && filesFallback && filesFallback.length > 0) {
        const file = filesFallback[0];
        if (file.type?.indexOf("image") !== -1) {
          imageItem = { getAsFile: () => file, type: file.type };
        }
      }

      if (!imageItem) {
        console.info("[RichTextEditor] no image item, allowing default paste", { debugId });
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") {
        e.stopImmediatePropagation();
      }
      console.info("[RichTextEditor] default paste prevented for image", { debugId });
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      console.info("[RichTextEditor] image item detected; delegating to onImagePaste", { debugId });

      const range = quill.getSelection() || { index: quill.getLength(), length: 0 };
      const baseIndex = range.index;
      const file = imageItem.getAsFile();

      const globalFallback =
        typeof window !== "undefined"
          ? window.__globalImagePasteHandler || window.__taskFormImagePaste || null
          : null;
      const pasteHandler = onImagePasteRef.current || globalFallback;
      console.info("[RichTextEditor] pasteHandler present?", !!pasteHandler, {
        debugId,
        usedGlobalFallback: !!(!onImagePasteRef.current && globalFallback),
      });

      const syncHtml = () => {
        const html = quill.root?.innerHTML || "";
        if (onChange) onChange(html);
        return html;
      };

      if (pasteHandler) {
        const helpers = {
          insertPlaceholder: (text) => {
            const targetIndex =
              quill.getSelection()?.index ?? baseIndex ?? quill.getLength();
            quill.focus();
            quill.insertText(targetIndex, text, { color: "#1677ff" }, "user");
            console.info("[RichTextEditor] inserted placeholder", { text, targetIndex, debugId });
            return { index: targetIndex, length: text.length, html: syncHtml() };
          },
          replacePlaceholderWithText: (placeholderRange, text) => {
            quill.deleteText(placeholderRange.index, placeholderRange.length, "user");
            quill.insertText(placeholderRange.index, text, "user");
            syncHtml();
          },
          replacePlaceholderWithImage: (placeholderRange, imageUrl) => {
            quill.deleteText(placeholderRange.index, placeholderRange.length, "user");
            quill.insertEmbed(placeholderRange.index, "image", imageUrl, "user");
            console.info("[RichTextEditor] placeholder replaced with image embed", {
              imageUrl,
              index: placeholderRange.index,
              debugId,
            });
            syncHtml();
          },
          getHtml: syncHtml,
        };

        pasteHandler(file, helpers);
      } else {
        const fallbackText = "![Uploading image…](upload://not-handled)";
        const targetIndex =
          quill.getSelection()?.index ?? baseIndex ?? quill.getLength();
        quill.focus();
        quill.insertText(targetIndex, fallbackText, "user");
        console.info("[RichTextEditor] onImagePaste not provided; inserted fallback placeholder", { debugId });
        syncHtml();
      }
  }, [debugId, onChange]);

  useEffect(() => {
    if (quillRef.current && !readOnly) {
      const quill = quillRef.current.getEditor();

      // Block any image content that Quill clipboard would normally insert
      const imageMatcher = (node, delta) => {
        if (node.tagName && node.tagName.toLowerCase() === "img") {
          const src = node.getAttribute("src") || "";
          const shouldBlock =
            src.startsWith("data:") ||
            src.startsWith("blob:") ||
            src.startsWith("upload://");
          if (shouldBlock) {
            return new Delta();
          }
        }
        return delta || new Delta();
      };
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, imageMatcher);

      // Add custom paste event listener in capture phase so we can fully
      // short-circuit Quill's default image paste handling.
      const editor = quill.root;
      editor.addEventListener("paste", handlePaste, true);

      return () => {
        editor.removeEventListener("paste", handlePaste, true);
        // Remove our matcher
        quill.clipboard.matchers = quill.clipboard.matchers.filter(
          ([, matcher]) => matcher !== imageMatcher
        );
      };
    }
  }, [readOnly, debugId, handlePaste]);

  return (
    <div className={`mb-3 ${className}`}>
      {label && <label className="form-label fw-semibold">{label}</label>}

      {/* Editor Border Wrapper */}
      <div style={{ position: 'relative' }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ""}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={readOnly}
          className="auto-grow-editor"
        />
      </div>
      
      {/* Optional: Add paste hint */}
      {!readOnly && (
        <small className="text-muted mt-1 d-block">
          You can paste images directly from clipboard (Ctrl+V / Cmd+V)
        </small>
      )}
    </div>
  );
};

export default RichTextEditor;
